import { Component, Input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { DynamicPageCreationComponent } from '../../webpagecreation/dynamic-page-creation/dynamic-page-creation.component';
import { AccordionPageService } from '../../../inputfileds/form_services/WebsiteManagement/accordian_page/accordion-page.service';
import {
  AccordionItem,
  ExpansionPanelComponent,
} from '../../webpagecreation/expansion-panel/expansion-panel.component';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicFileUploadComponent } from '../../webpagecreation/dynamic-file-upload/dynamic-file-upload.component';
import { environment } from '../../../../environments/environment';
import { TranslateDirective } from '../../Utilites/Directives/translate.directive';
import { TranslatePipe } from '../../Utilites/Pipe/translate.pipe';

@Component({
  selector: 'app-accordion-dynamic-page',
  templateUrl: './accordion-dynamic-page.component.html',
  styleUrls: ['./accordion-dynamic-page.component.scss'],
  standalone: true,
  imports: [
    MatExpansionModule,
    MatButtonModule,
    ReactiveFormsModule,
    CommonModule,
    MatMenuModule,
    MatCardModule,
    ExpansionPanelComponent,
    TranslateDirective,
  ],
})
export class AccordionDynamicPageComponent {
  @Input() websiteId!: string;
  @Input() pageId!: string;
  @Input() apiEndpoint!: string;
  readonly panelOpenState = signal(false);

  accordionItems: AccordionItem[] = [];
  selectedId: any;
  private expandedItemIds: Set<string> = new Set();

  constructor(
    private dialog: MatDialog,
    private questionService: AccordionPageService,
    private apiService: ApiservicesService,
    private route: ActivatedRoute,
    private Router: Router,
  ) {}

  openDynamicPageCreationDialog(parentItem?: AccordionItem) {
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '600px',
      data: {
        pageTitle: parentItem ? 'Add Nested Accordion' : 'Create Accordion',
        questions$: this.questionService.getCreateAccordionPage(),
        initialData: null,
        TabButton: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const payload: any = {
          title: result.title,
          description: result.description,
          website_id: this.websiteId,
          website_page_id: this.pageId,
          open_in_new_tab: result.open_in_new_tab || false,
          data: (result.data ?? []).map((dataItem: any) => ({
            data_type_id: String(dataItem.id),
          })),
        };
        if (parentItem && parentItem.id) {
          payload.parent_accordion_id = parentItem.id;
          this.expandedItemIds.add(String(parentItem.id));
        }

        this.apiService.createAccordionPage(payload).subscribe({
          next: () => {
            this.getAccordionItems();
          },
          error: () => {},
        });

        // Optimistic UI update
        const newAccordion: AccordionItem = {
          title: result.title,
          type: 'accordion',
          children: [],
        };
        if (parentItem) {
          parentItem.children = parentItem.children || [];
          parentItem.children.push(newAccordion);
        } else {
          this.accordionItems.push(newAccordion);
        }
      }
    });
  }

  getAccordionItems(): void {
    this.apiService.getAccordionItems(this.websiteId, this.pageId).subscribe({
      next: (response: { total_count: number; pages: number; rows: any[] }) => {
        const itemsArray = response?.rows || [];

        const transformed = itemsArray.map((item: any) =>
          this.transformToAccordionItem(item),
        );

        this.accordionItems = [...transformed];
      },
      error: (error: any) => {
        console.error('❌ Error fetching accordion items:', error);
      },
    });
  }

  private transformToAccordionItem(apiItem: any): AccordionItem {
    const children: AccordionItem[] = [];

    if (Array.isArray(apiItem.data) && apiItem.data.length > 0) {
      apiItem.data.forEach((child: any) => {
        children.push(this.transformToAccordionItem(child));
      });
    }
    let links: any[] = [];
    if (
      Array.isArray(apiItem.accordion_content) &&
      apiItem.accordion_content.length > 0
    ) {
      links = apiItem.accordion_content.map((content: any, idx: number) => ({
        id: content.accordion_details_id,
        label: content.type === 'FILES' ? `File ${idx + 1}` : `Link ${idx + 1}`,
        url: content.content || '#',
        description: `Type: ${content.type}`,
        icon: content.type === 'FILES' ? 'fas fa-file' : 'fas fa-link',
      }));
    }

    const itemId = apiItem.id?.toString();
    const isExpanded = this.expandedItemIds.has(itemId);
    return {
      id: apiItem.id?.toString(),
      title: apiItem.title || apiItem.name || 'Untitled Accordion',
      description: apiItem.description || '',
      type: 'accordion',
      expanded: false,
      actions: true,
      actionTooltip: 'Page actions',
      children: children,
      links: links.length > 0 ? links : undefined,
      data: apiItem,
    };
  }

  ngOnInit() {
    this.websiteId = this.route.snapshot?.queryParams['websiteId'];
    this.pageId = this.route.snapshot?.queryParams['pageId'];
    this.apiEndpoint = this.route.snapshot?.queryParams['endpoint'];
    this.getAccordionItems();
  }

  onActionClicked(event: { action: string; item: AccordionItem }) {
    if (event.action === 'addAccordion') {
      this.openDynamicPageCreationDialog(event.item);
      return;
    }
    if (event.action === 'uploadFile') {
      this.openFileUploadDialog(event.item);
      return;
    }
    if (event.action === 'link') {
      this.openLinkDialog(event.item);
      return;
    }
    if (event.action === 'delete') {
      console.log('Delete action for item:', event.item);
      if (event.item.type === 'links') {
        this.deleteAccordionContent(event.item.data.id, 'link');
      } else {
        this.deleteAccordionContent(event.item.id, 'accordion');
      }
      return;
    }
    if (event.action === 'edit') {
      this.editPage(event.item);
      return;
    }
  }

  onItemExpanded(item: AccordionItem) {
    this.selectedId = item.id;
    if (item.id) {
      this.expandedItemIds.add(String(item.id));
    }
  }
  onItemCollapsed(item: AccordionItem) {
    if (item.id) {
      this.expandedItemIds.delete(String(item.id));
    }
  }

  openFileUploadDialog(parentItem?: AccordionItem) {
    const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
      width: '500px',
      data: {
        title: parentItem ? 'Upload File to Accordion' : 'Upload File',
        service: this.apiService,
        questions$: this.questionService.getCreateAccordionPage(),
        item: parentItem,
        isLink: false,
        id: parentItem?.id,
        accessUrl: environment.ASSET_BLOB_URL,
        containerName: environment.ASSET_CONTAINER,
        folderDataPath: '/WEBSITE_MANAGEMENT/AccordionFiles',
      },
    });

    dialogRef.componentInstance.formSubmit.subscribe((formData: FormData) => {
      if (parentItem?.id) {
        this.expandedItemIds.add(String(parentItem.id));
      }
      this.apiService.updateAccordionData(formData, parentItem?.id).subscribe({
        next: () => {
          this.getAccordionItems();
        },
        error: () => {},
      });
      dialogRef.close();
    });

    dialogRef.componentInstance.cancel.subscribe(() => {
      dialogRef.close();
    });
  }

  openLinkDialog(item: AccordionItem) {
    const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
      width: '400px',
      data: {
        title: 'Add Link',
        service: this.apiService,
        questions$: this.questionService.getLinkAccordionData(),
        item,
        id: item.id,
        isLink: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (item.id) {
          this.expandedItemIds.add(String(item.id));
        }
        this.getAccordionItems();
        console.log('Link dialog result:', result);
      }
    });
  }

  editPage(item: AccordionItem) {
    console.log('Edit page:', item.title, item.id);
    this.openEditDialog(item);
  }

  deleteAccordionContent(
    accordionDetailsId?: string | number,
    itemType?: string,
  ) {
    const payload =
      itemType === 'link'
        ? { accordion_details_id: accordionDetailsId }
        : { accordion_id: accordionDetailsId };

    if (confirm('Are you sure you want to delete this?')) {
      this.apiService.deleteAccordionData(payload).subscribe({
        next: () => {
          this.getAccordionItems();
        },
        error: (error) => {
          console.error('Error deleting item:', error);
        },
      });
    }
  }

  openEditDialog(item: AccordionItem) {
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '600px',
      data: {
        pageTitle: 'Edit Accordion',
        questions$: this.questionService.getCreateAccordionPage(),
        initialData: item.data,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updatedData = {
          ...result,
          id: item.id,
          data: (result.data ?? []).map((dataItem: any) => ({
            data_type_id: String(dataItem.id),
          })),
          website_id: this.websiteId,
          website_page_id: this.pageId,
        };
        if (item.id) {
          this.expandedItemIds.add(String(item.id));
        }
        this.apiService.updateAccordionData(updatedData, item.id).subscribe({
          next: (response) => {
            console.log('Accordion page updated successfully:', response);
            this.getAccordionItems();
          },
          error: (error) => {
            console.error('Error updating accordion page:', error);
          },
        });
      }
    });
  }
  goBack(): void {
    this.Router.navigate([`/websites/WebcomponentList`], {
      queryParams: { websiteId: this.websiteId, pageId: this.pageId },
    });
  }
}
